import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AddPlus2 } from '../../assets/icons';
import { PRICING_TAB_VALUES, URL_SEARCH_PARAM_KEY } from '../../constant/UrlSearchParams.constant';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyFeePackage } from '../../hooks/useCompanyFeePackage';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useAllowedFeatures } from '../../hooks/useFeaturePersonalization';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { useShowBankingInfoModal } from '../../hooks/useShowBankingInfoModal';
import { RoutePath } from '../../routes';
import { ChargerOptions, useFetchChargersQuery } from '../../services/charger.api';
import { useFetchPricingModelsQuery } from '../../services/pricing.api';
import { Charger } from '../../stores/types';
import { ChargerSelection, ChargersGroupByLocation } from '../../stores/types/chargers.interface';
import { Price } from '../../stores/types/price.interface';
import { DiscountForm } from '../Discounts/DiscountForm/DiscountForm.component';
import { Discounts } from '../Discounts/Discounts.component';
import Promotions from '../Promotion/Promotions.component';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  MODAL_TYPES,
  Tab,
  Tabs,
  useGlobalModalContext,
} from '../_ui';
import { RuleComponent } from './Rule.component';

enum TABS {
  PRICING_RULES = 0,
  DISCOUNT,
  PROMOTIONS,
}

const TAB_FROM_URL: Record<string, TABS> = {
  [PRICING_TAB_VALUES.PRICING_RULES]: TABS.PRICING_RULES,
  [PRICING_TAB_VALUES.DISCOUNT]: TABS.DISCOUNT,
  [PRICING_TAB_VALUES.PROMOTIONS]: TABS.PROMOTIONS,
};

export const PricingManagement = () => {
  const { t } = useTranslation();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();
  const { showModal } = useGlobalModalContext();
  const { showBankInfoModal, displayBankInfoModal } = useShowBankingInfoModal();
  const { isFlexPlan, isNonePlan, sessionFee } = useCompanyFeePackage();
  const isDiscountRestrictedToFree = (isFlexPlan || isNonePlan) && Number(sessionFee) > 0.0;

  const [searchParams] = useSearchParams();
  const initialTab =
    TAB_FROM_URL[searchParams.get(URL_SEARCH_PARAM_KEY.PRICING_TAB) ?? ''] ?? TABS.PRICING_RULES;
  const [selectedTab, setSelectedTab] = useState<number>(initialTab);
  const { companyId } = useCompany();
  const { canCreatePricingRules, canCreateDiscounts, canCreatePromotion } =
    useCompanyPermissions(companyId);
  const { allowPromotions } = useAllowedFeatures();

  const fetchOption = useMemo(() => {
    return {
      includeAcl: true,
    } as ChargerOptions;
  }, []);

  const { chargers } = useFetchChargersQuery(fetchOption, {
    selectFromResult: ({ data, isSuccess }) => {
      return {
        chargers: data ? data.entities : [],
        isChargerFetchingSuccess: isSuccess,
      };
    },
  });

  const { userScope } = useCompany();
  const { pricingData, totalNonSyntheticChargers } = useFetchPricingModelsQuery(
    { companyId: companyId!, scope: userScope! },
    {
      selectFromResult: (endPoint) => ({
        pricingData: endPoint.data ? endPoint.data : [],
        totalNonSyntheticChargers: endPoint.data
          ? endPoint.data
              .filter((pricing) => pricing.synthetic === false)
              .reduce((previous, current) => previous + current.chargers.length, 0)
          : 0,
        isPricingLoading: endPoint.isFetching,
      }),
    },
  );

  const chargersGroupByLocation: ChargersGroupByLocation = useMemo(() => {
    return _.groupBy(chargers, 'location.name');
  }, [chargers]);

  const chargersList = useMemo(() => {
    const data = Object.entries(chargersGroupByLocation).map((key: Array<any>) => {
      const children: ChargerSelection = key[1].map((charger: Charger) => {
        return {
          id: charger.id,
          label: charger.name,
          selected: false,
        };
      });

      return {
        id: key[0],
        label: key[0],
        selected: false,
        children: children || [],
      };
    });
    return data.filter((charger: any) => charger.children.length);
  }, [chargersGroupByLocation]);

  const allChargerList = useMemo(() => {
    const list = _.cloneDeep(chargersList);
    const all = {
      id: 'all',
      label: 'All chargers',
      selected: false,
      children: [],
    };
    list.unshift(all as any);
    return list;
  }, [chargersList]);

  const sortedPricing = useMemo(
    () => [...pricingData].sort((item) => (item.defaultPrice ? -1 : 1)),
    [pricingData],
  );

  const renderTopRightButtons = useMemo(() => {
    const renderEditPricingRuleComponent = () => {
      navigateWithCompanyId(`../${RoutePath.PRICING_EDIT}`, companyId);
    };

    const handleAddClick = () => {
      showModal(MODAL_TYPES.INFO_MODAL, {
        title: t('discount_add_modal_title'),
        width: '540px',
        height: 'max-content',
        onRenderBody: () => (
          <DiscountForm
            chargersList={allChargerList}
            isDiscountRestrictedToFree={isDiscountRestrictedToFree}
          />
        ),
        shouldCloseOnOverlayClick: false,
      });
    };

    const handleAddPromotionClick = () => {
      navigateWithCompanyId(`../${RoutePath.PROMOTION_NEW}`, companyId);
    };

    if (selectedTab === TABS.PRICING_RULES && canCreatePricingRules) {
      return (
        <Button
          key='addRule'
          icon={AddPlus2}
          label={t('pricing_add_rule')}
          type={ButtonType.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={renderEditPricingRuleComponent}
          dataTestId='addRule'
        />
      );
    }

    if (selectedTab === TABS.DISCOUNT && canCreateDiscounts) {
      return (
        <Button
          key='addDiscount'
          icon={AddPlus2}
          label={t('discount_add_modal_title')}
          type={ButtonType.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={handleAddClick}
          dataTestId='addDiscount'
        />
      );
    }

    if (selectedTab === TABS.PROMOTIONS && canCreatePromotion && allowPromotions) {
      return (
        <Button
          key='addPromotion'
          icon={AddPlus2}
          label={t('add_promotion_title')}
          type={ButtonType.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={handleAddPromotionClick}
          dataTestId='addPromotion'
        />
      );
    }

    return null;
  }, [
    allChargerList,
    canCreatePricingRules,
    canCreateDiscounts,
    companyId,
    navigateWithCompanyId,
    selectedTab,
    showModal,
    t,
    isFlexPlan,
    isNonePlan,
    sessionFee,
    canCreatePromotion,
    allowPromotions,
  ]);

  /**
   * Show bank info modal if company's banking info missing & have at least one non-free pricing rule.
   */
  useEffect(() => {
    if (showBankInfoModal) {
      displayBankInfoModal();
    }
  }, [showBankInfoModal, displayBankInfoModal]);

  return (
    <Card dataTestId='pricingRules'>
      <div className=' flex flex-row justify-between pb-4'>
        <Tabs onTabChange={setSelectedTab} defaultIndex={initialTab} rootClass='w-[420px]'>
          <Tab label={t('pricing_rules')} />
          <Tab label={t('discounts')} dataTestId='discountTab' />
          {allowPromotions ? <Tab label={t('promotions')} dataTestId='promotionTab' /> : null}
        </Tabs>
        {renderTopRightButtons}
      </div>

      {selectedTab === TABS.PRICING_RULES ? (
        sortedPricing?.length === 0 ? (
          <RuleComponent
            pricingData={{} as Price}
            totalNonSyntheticChargers={0}
            isPricingLoading
            totalDataLength={sortedPricing.length}
            sessionFee={sessionFee}
          />
        ) : (
          sortedPricing?.map((pricingResponse: Price) => (
            <RuleComponent
              key={pricingResponse.id}
              pricingData={pricingResponse}
              totalNonSyntheticChargers={totalNonSyntheticChargers}
              totalDataLength={sortedPricing.length}
              sessionFee={sessionFee}
            />
          ))
        )
      ) : selectedTab === TABS.DISCOUNT ? (
        <Discounts
          chargersList={chargersList}
          allChargerList={allChargerList}
          isDiscountRestrictedToFree={isDiscountRestrictedToFree}
        />
      ) : selectedTab === TABS.PROMOTIONS ? (
        <Promotions />
      ) : null}
    </Card>
  );
};
